import React, { useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { RootState, AppDispatch } from './app/store'
import { loadSettings } from './features/settings/settingsSlice'
import { setLoading } from './features/auth/authSlice'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { useWindowSize } from './hooks/useWindowSize'
import apiService from './services/api'
import { menuApi } from './features/menu/menuAPI'
import { cartApi } from './features/cart/cartAPI'
import { ordersApi } from './features/orders/ordersAPI'
import { profileApi } from './features/profile/profileAPI'
import { customerApi } from './features/customer/customerAPI'
import { tablesApi } from './features/tables/tablesAPI'
import { fetchMenuData } from './features/menu/menuSlice'
import { loadCartItems } from './features/cart/cartItemsSlice'

// Import pages
import LoginPage from './pages/auth_pages/LoginPage'
import LoginPinPage from './pages/auth_pages/LoginPinPage'
import ShiftOpeningPage from './pages/auth_pages/ShiftOpeningPage'
import HomePage from './pages/HomePage'

import OrdersPage from './pages/OrdersPage'
import CollectionPage from './pages/CollectionPage'
import OrderDetailsPage from './pages/OrderDetailsPage'
import OrderConfirmationPage from './pages/OrderConfirmationPage'
import PaymentPage from './pages/PaymentPage'
import PartialPaymentPage from './pages/PartialPaymentPage'
import ProfilePage from './pages/ProfilePage'

import OfflinePage from './pages/OfflinePage'

// Import components
import ProtectedRoute from './components/ProtectedRoute'
import LoadingSpinner from './components/LoadingSpinner'
import AppLayout from './components/layout/AppLayout'
import ScreenLock from './components/ScreenLock'
import OnOffTenantHeader from './components/tenant/OnOffTenantHeader'

function App() {
  const dispatch = useDispatch<AppDispatch>()
  const { i18n } = useTranslation()
  const location = useLocation()
  const settingsLoadedRef = useRef(false)
  const isOnline = useOnlineStatus()
  const { width } = useWindowSize()
  
  const { isLoading: authLoading, isAuthenticated, user, token } = useSelector((state: RootState) => state.auth)
  const { isLoading: settingsLoading, settings } = useSelector((state: RootState) => state.settings)



  useEffect(() => {
    // Load settings on app start (only once)
    if (!settingsLoadedRef.current) {
      settingsLoadedRef.current = true
      dispatch(loadSettings() as any).catch((error) => {
      })
    }
    
    // Reset auth loading state if it's stuck (likely from persisted state)
    // Only reset if there's no active login operation
    if (authLoading && !token && !user) {
      
      dispatch(setLoading(false))
    }
    
    // Check if token exists and set it in Axios
    const tokenFromStorage = localStorage.getItem('auth_token')
    const userData = localStorage.getItem('user_data')
 
    
    if (tokenFromStorage) {
      // Set token in Axios headers
      apiService.defaults.headers.Authorization = `Bearer ${tokenFromStorage}`

    }
  }, [dispatch, authLoading, token, user])

  useEffect(() => {
    // Update document direction based on language - matches Flutter exactly
    const isRTL = settings.language === 'ar'
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = settings.language
    
    // Update i18n language - matches Flutter exactly
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
    
    // Update body class for RTL/LTR support - matches Flutter exactly
    document.body.classList.toggle('rtl', isRTL)
    document.body.classList.toggle('ltr', !isRTL)
    
    // Refresh all API data when language changes
    // This ensures data is fetched with the correct language header
    const refreshAllData = () => {
      try {

        
        // Don't refresh cart if on order confirmation page
        const isOnOrderConfirmation = location.pathname === '/order-confirmation'
        
        // Dispatch invalidate tags for all APIs to force refetch
        dispatch(menuApi.util.invalidateTags(['Categories', 'Products']))
        if (!isOnOrderConfirmation) {
          dispatch(cartApi.util.invalidateTags(['Cart']))
        }
        dispatch(ordersApi.util.invalidateTags(['Orders']))
        dispatch(profileApi.util.invalidateTags(['Profile']))
        dispatch(customerApi.util.invalidateTags(['Customer']))
        dispatch(tablesApi.util.invalidateTags(['Tables']))
        
        // Also refresh thunk-based API calls (but skip cart on order confirmation)
        dispatch(fetchMenuData() as any)
        if (!isOnOrderConfirmation) {
          dispatch(loadCartItems({ forceRefresh: true }) as any)
        }
      } catch (error) {
     
      }
    }
    
    // Only refresh if language actually changed (not on initial load)
    const previousLanguage = localStorage.getItem('previous_language')
    if (previousLanguage && previousLanguage !== settings.language) {
      refreshAllData()
    }
    localStorage.setItem('previous_language', settings.language)
    
    // Also listen for custom language change event for immediate refresh
    const handleLanguageChangeEvent = (event: CustomEvent) => {
      const newLanguage = event.detail?.language
      if (newLanguage && newLanguage !== previousLanguage) {
        refreshAllData()
      }
    }
    
    window.addEventListener('languageChanged', handleLanguageChangeEvent as (event: Event) => void)
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChangeEvent as (event: Event) => void)
    }
  }, [settings.language, i18n, dispatch, location.pathname])

  // Show loading spinner while initializing
  // Add timeout to prevent infinite loading (max 3 seconds)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  
  useEffect(() => {
    if (authLoading || settingsLoading) {
      const timer = setTimeout(() => {
      
        setLoadingTimeout(true)
      }, 3000)
      return () => clearTimeout(timer)
    } else {
      setLoadingTimeout(false)
    }
  }, [authLoading, settingsLoading])

  if ((authLoading || settingsLoading) && !loadingTimeout) {
    
    return <LoadingSpinner />
  }

  // Show offline page if no internet connection
  if (!isOnline) {

    return <OfflinePage />
  }

  // Show screen lock if window width is less than 800px
  if (width < 800) {
  
    return <ScreenLock />
  }

 

  const isRTL = settings.language === 'ar'

  return (
    <div className={`app-shell bg-flutter ${isRTL ? 'rtl' : 'ltr'}`}>
      <OnOffTenantHeader />

      <div className="app-shell__content">
      <Routes>
        {/* Public routes - matches Flutter exactly */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login-pin" element={<LoginPinPage />} />
        
        {/* Protected routes - matches Flutter exactly */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="home" element={<HomePage />} />
           
            <Route path="orders" element={<OrdersPage />} />
            <Route path="collection" element={<CollectionPage />} />
            <Route path="orders/:orderId" element={<OrderDetailsPage />} />
            <Route path="order-confirmation" element={<OrderConfirmationPage />} />
            <Route path="payment" element={<PaymentPage />} />
            <Route path="partial-payment/:orderId" element={<PartialPaymentPage />} />
            <Route path="profile" element={<ProfilePage />} />
        
            {/* Shift opening route - now protected */}
            <Route path="shift-opening" element={<ShiftOpeningPage />} />
          </Route>
        </Route>
        
        {/* Catch all route - matches Flutter exactly */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </div>
    </div>
  )
}

export default App